import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, CircleCheck, OctagonX, TriangleAlert, X } from "lucide-react";
import { I18n } from "@/i18n/I18n";
import { Button } from "@/frontend/components/ui/button";
import { ToastClose, ToastContent, ToastDescription, ToastTitle } from "@/frontend/components/ui/toast";
import { FusaToast, FusaToastType } from "./types";

const ICONS = {
  [FusaToastType.SUCCESS]: <CircleCheck className="size-5 text-[var(--green-600)]" aria-hidden />,
  [FusaToastType.WARNING]: <TriangleAlert className="size-5 text-[var(--yellow-600)]" aria-hidden />,
  [FusaToastType.ERROR]: <OctagonX className="size-5 text-destructive" aria-hidden />,
};

/**
 * The layout every toast shares: icon, title and message, then any link or
 * details the toast carries, and the close X. A port of Veriform Desktop's
 * StatusToast, so toasts look the same in both apps; keep the two in step.
 *
 * Desktop's classes carry a `tw:` prefix; without it, `gap-3` and `text-primary`
 * would be Bootstrap's `!important` utilities, so they are spelled `gap-[0.75rem]`
 * and `text-[color:var(--primary)]` here (docs/STYLING.md).
 */
export function StatusToast({ toast }: { toast: FusaToast }) {
  const [showDetails, setShowDetails] = useState(false);
  const detailsLabel = I18n.get(showDetails ? 'hideDetails' : 'showDetails');

  return (
    <ToastContent className="items-start gap-[0.75rem]">
      <span className="mt-0.5 shrink-0">{ICONS[toast.type]}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 wrap-break-word">
        <ToastTitle className="m-0 leading-5">{toast.header}</ToastTitle>
        {toast.message && (
          <ToastDescription className="m-0 max-h-40 overflow-y-auto leading-5">{toast.message}</ToastDescription>
        )}
        {toast.link && toast.linkText && (
          <Link className="text-sm font-medium text-[color:var(--primary)] underline-offset-4 hover:underline" to={toast.link}>
            {toast.linkText}
          </Link>
        )}
        {toast.details && (
          <div className="text-sm">
            <Button
              variant="link"
              size="sm"
              className="h-auto gap-0.5 p-0 text-sm text-muted-foreground"
              title={detailsLabel}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <ChevronDown /> : <ChevronRight />}
              <span>{detailsLabel}</span>
            </Button>
            {showDetails && (
              <div className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap text-muted-foreground">{toast.details}</div>
            )}
          </div>
        )}
      </div>
      <ToastClose aria-label={I18n.get('close')} className="-mt-1">
        <X className="size-5 text-[var(--neutral-600)]" />
      </ToastClose>
    </ToastContent>
  );
}
